import {useState} from 'react'
import type {TFunction} from 'i18next'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {FichaDatos, type BloqueFicha} from '@/components/ui/ficha-datos'
import {Tag} from '@/components/ui/tag'
import {AccessResult} from '@/components/access-result'
import {CorrectEmailDialog} from '@/components/correct-email-dialog'
import {CorreoSinConfirmar} from '@/components/correo-sin-confirmar'
import {EmployeeFormDialog} from '@/components/employee-form-dialog'
import {InactiveBadge} from '@/components/inactive-badge'
import {KairosMark} from '@/components/kairos-mark'
import {Eyebrow} from '@/components/fact'
import {HelpIcon} from '@/components/help-icon'
import {tocaElAccesoDe, useGrantAccess} from '@/hooks/platform/use-access'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {translateApiError} from '@/lib/errors'

// Un dato que falta es información: la fila se queda y el valor es la raya.
const VACIO = <span className="text-muted-foreground">—</span>

function GrantAccessSection({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const grant = useGrantAccess(businessId)
  const [error, setError] = useState<string | null>(null)

  if (grant.data) return <AccessResult grant={grant.data} />

  return (
    <div className="space-y-2">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button
        type="button"
        size="sm"
        data-testid="grant-access-submit"
        disabled={grant.isPending}
        onClick={() => {
          setError(null)
          grant.mutate(employeeId, {onError: (err) => setError(translateApiError(err))})
        }}>
        {t('access.grantSubmit')}
      </Button>
    </div>
  )
}

function bloquesDeIdentidad({
  t,
  employee,
  cobertura,
  businessId,
  canGrantAccess,
}: {
  t: TFunction
  employee: Employee
  cobertura: {covered: number; total: number} | null
  businessId: string
  canGrantAccess: boolean
}): BloqueFicha[] {
  return [
    {
      clave: 'identidad',
      titulo: t('employees.identidad.bloque'),
      testId: 'employee-identidad',
      ayuda: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('employees.identidad.bloque')})}
          testId="employee-identidad-ayuda">
          <dl className="space-y-1.5">
            <dt className="font-medium">{t('employees.fields.vinculo')}</dt>
            <dd data-testid="employee-identidad-ayuda-vinculo">{t(`vinculo.tooltip.${employee.vinculo}`)}</dd>
            <dt className="font-medium">{t('employees.table.coverage')}</dt>
            <dd>{t('employees.coverageSheetHelp')}</dd>
            <dt className="font-medium">{t('employees.identidad.correoSinConfirmar')}</dt>
            <dd data-testid="employee-identidad-ayuda-correo">{t('employees.identidad.correoSinConfirmarHelp')}</dd>
            {employee.verificacion_identidad && (
              <>
                <dt className="font-medium">{t(`verificacionIdentidad.chip.${employee.verificacion_identidad}`)}</dt>
                <dd data-testid="employee-identidad-ayuda-verificacion">{t('verificacionIdentidad.ayuda')}</dd>
              </>
            )}
          </dl>
        </HelpIcon>
      ),
      filas: [
        {
          clave: 'role',
          etiqueta: t('employees.identidad.acceso'),
          valor: <Tag tone="neutral">{t(`roles.${employee.role}`)}</Tag>,
        },
        ...(cobertura
          ? [
              {
                clave: 'cobertura',
                etiqueta: t('employees.table.coverage'),
                valor: (
                  <Tag tone="neutral" data-testid="detail-coverage">
                    {cobertura.covered}/{cobertura.total}
                  </Tag>
                ),
              },
            ]
          : []),
        {
          clave: 'vinculo',
          // Tono neutro: el vínculo es identidad y la escala de estado es para lo que exige acción (U2).
          etiqueta: t('employees.fields.vinculo'),
          valor: (
            <Tag tone="neutral" data-testid="employee-vinculo">
              {t(`vinculo.${employee.vinculo}`)}
            </Tag>
          ),
        },
        {
          clave: 'email',
          etiqueta: t('employees.identidad.correo'),
          valor: (
            <span className="flex flex-wrap items-center gap-1" data-testid="employee-identity-email">
              <span className="break-all">{employee.email || VACIO}</span>
              {/* Sin cuenta activa, el correo es el canal de la invitación y lo corrige el operador;
                  después es de la persona. */}
              {canGrantAccess && !employee.tiene_acceso && (
                <CorrectEmailDialog businessId={businessId} employeeId={employee.id} email={employee.email} />
              )}
              {/* Cuenta reclamada sin correo confirmado: no recupera la contraseña por correo (KAI-139). */}
              {employee.correo_sin_confirmar && (
                <CorreoSinConfirmar businessId={businessId} employeeId={employee.id} puedeReenviar={canGrantAccess} />
              )}
            </span>
          ),
        },
        {clave: 'phone', etiqueta: t('employees.identidad.telefono'), valor: employee.phone || VACIO},
        {clave: 'sede', etiqueta: t('ficha.sede'), valor: employee.sede?.name ?? VACIO},
      ],
    },
  ]
}

export function EmployeeIdentityCard({
  businessId,
  employee,
  fullName,
  isOwner,
  canOperate,
  canOperateEmployee,
  canGrantAccess,
  canManagePeople,
  godReadOnly,
  entrevistaCompleta,
  cobertura,
  resolicitarError,
  onResolicitar,
  onDelete,
}: {
  businessId: string
  employee: Employee
  fullName: string
  isOwner: boolean
  canOperate: boolean
  canOperateEmployee: boolean
  canGrantAccess: boolean
  canManagePeople: boolean
  godReadOnly: boolean
  entrevistaCompleta: boolean
  /** Estándares SG-SST con soporte de esta persona sobre los que le aplican. */
  cobertura: {covered: number; total: number} | null
  resolicitarError: string | null
  onResolicitar: () => void
  onDelete: () => void
}) {
  const {t} = useTranslation()
  const [grantingAccess, setGrantingAccess] = useState(false)
  // Kairos no guarda retratos: el monograma es lo que distingue a la persona.
  const initials = `${employee.name.charAt(0)}${employee.last_name.charAt(0)}`.toUpperCase()
  // Retenida (KAI-438), la cuenta no es de esta empresa: no hay acceso que dar ni correo que corregir.
  const accesoTocable = canGrantAccess && tocaElAccesoDe(employee.role, isOwner) && !employee.verificacion_identidad
  const bloques = bloquesDeIdentidad({t, employee, cobertura, businessId, canGrantAccess: accesoTocable})

  return (
    <aside className="surface xl:sticky xl:top-2">
      <div className="surface-divider border-b p-5">
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="flex size-12 shrink-0 items-center justify-center rounded-full bg-secondary font-heading text-base text-primary">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-foreground">
              <KairosMark />
              <h1 className="font-heading text-xl break-words">{fullName}</h1>
            </div>
            <p className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{employee.cedula}</span>
              {employee.status === 'active' ? (
                <Tag tone="neutral">{t('employees.status.active')}</Tag>
              ) : (
                <InactiveBadge since={employee.deactivated_at} />
              )}
              {employee.verificacion_identidad && (
                <Tag
                  tone={employee.verificacion_identidad === 'pendiente' ? 'amber' : 'red'}
                  data-testid="employee-verificacion-identidad">
                  {t(`verificacionIdentidad.chip.${employee.verificacion_identidad}`)}
                </Tag>
              )}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid={`onboarding-chips-${employee.id}`}>
          <Tag tone="green" data-testid={`onboarding-chip-registro-${employee.id}`} data-done="true">
            {t('employees.onboarding.registro')}
          </Tag>
          <Tag
            tone={employee.onboarding_estado !== 'registro_basico' ? 'green' : 'neutral'}
            data-testid={`onboarding-chip-ficha-${employee.id}`}
            data-done={employee.onboarding_estado !== 'registro_basico'}>
            {t('employees.onboarding.ficha')}
          </Tag>
          <Tag
            tone={employee.onboarding_estado === 'entrevista_completa' ? 'green' : 'neutral'}
            data-testid={`onboarding-chip-entrevista-${employee.id}`}
            data-done={employee.onboarding_estado === 'entrevista_completa'}>
            {t('employees.onboarding.entrevista')}
          </Tag>
          {employee.onboarding_pendiente_aprobacion && (
            <Tag tone="amber" data-testid={`onboarding-pending-${employee.id}`}>
              {t('employees.onboarding.pendingChip')}
            </Tag>
          )}
        </div>
      </div>

      <div className="p-5">
        <FichaDatos bloques={bloques} testId="employee-identidad-datos" />
      </div>

      {grantingAccess && (
        <div className="surface-divider border-t p-5">
          <Eyebrow>{t('access.grantTitle', {name: fullName})}</Eyebrow>
          <GrantAccessSection businessId={businessId} employeeId={employee.id} />
        </div>
      )}

      <div className="surface-divider space-y-2 border-t p-5">
        <div className="flex flex-wrap gap-2">
          {canOperateEmployee && (
            <EmployeeFormDialog
              businessId={businessId}
              employee={employee}
              canAssignRole={isOwner}
              trigger={
                <Button variant="outline" size="sm">
                  {t('employees.edit.title')}
                </Button>
              }
            />
          )}
          {accesoTocable && employee.status === 'active' && !grantingAccess && !employee.tiene_acceso && (
            <Button variant="outline" size="sm" data-testid="grant-access-open" onClick={() => setGrantingAccess(true)}>
              {t('access.grant')}
            </Button>
          )}
          {!godReadOnly && canOperate && (
            <Button
              variant="outline"
              size="sm"
              data-testid="employee-resolicitar-evaluacion"
              disabled={!entrevistaCompleta}
              title={!entrevistaCompleta ? t('ipevr.resolicitar.notCompleted') : undefined}
              onClick={onResolicitar}>
              {t('ipevr.resolicitar.action')}
            </Button>
          )}
          {canManagePeople && (
            <Button variant="destructive" size="sm" data-testid="employee-detail-delete" onClick={onDelete}>
              {t('employees.delete')}
            </Button>
          )}
        </div>
        {resolicitarError && <p className="text-sm text-destructive">{resolicitarError}</p>}
      </div>
    </aside>
  )
}
